/**
 * HTTP transport functions injected into the Orval-generated API client.
 *
 * Orval calls a custom transport function a "mutator." Instead of generating a direct
 * `fetch` call, each generated operation imports one of the exported functions below and
 * passes it the operation's root-relative path and request options. The term does not mean
 * that the HTTP operation changes server state: generated GET operations use a mutator too.
 *
 * These functions connect generated paths, methods, bodies, and result types to application
 * runtime policy: the backend origin, cookie credentials, status-bearing errors, response
 * parsing, exact no-content acknowledgements, and the status/header-preserving avatar
 * response. Orval bundles this file for an ES5 target, so keep its implementation within the
 * syntax enforced by the generator tests.
 */
import { API_URL } from '@/lib/consts';

/** Error shape threaded through Orval's generated SWR hook signatures. */
type ErrorType<ErrorData> = Error & {
  data?: ErrorData;
  responseReadFailed?: boolean;
  status?: number;
};

/** Per-operation transport options understood by the generated client mutator. */
type OrvalRequestInit = RequestInit & { parseErrorBody?: boolean };

/** Parses an optional error body without masking the original HTTP failure. */
function parseErrorData(body: string): unknown {
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return undefined;
  }
}

/** Treats absent and plain-text test headers as JSON unless a binary media type is explicit. */
function responseUsesJson(response: Response): boolean {
  var headers = (response as Partial<Response>).headers;
  var contentType = headers?.get?.('content-type') ?? null;
  return (
    contentType === null ||
    contentType.includes('application/json') ||
    contentType.startsWith('text/plain')
  );
}

/** Reads an error body from real Responses and the minimal response doubles used by UI tests. */
function responseText(response: Response): Promise<string> {
  if (typeof response.text === 'function') return response.text();
  if (typeof response.json === 'function') {
    return response.json().then((value: unknown) => JSON.stringify(value));
  }
  return Promise.resolve('');
}

/** Builds the shared status-bearing transport error without exposing response bodies in its text. */
function httpError(
  response: Response,
  data?: unknown,
  responseReadFailed?: boolean
): ErrorType<unknown> {
  return Object.assign(
    new Error(`HTTP ${response.status}${response.statusText ? `: ${response.statusText}` : ''}`),
    { status: response.status, data, ...(responseReadFailed ? { responseReadFailed: true } : {}) }
  );
}

/** Marks a successful response-body failure without rewriting its native error details. */
function markResponseReadFailure(error: unknown): Error & { responseReadFailed: true } {
  var readableError = error instanceof Error ? error : new Error(String(error));
  return Object.assign(readableError, { responseReadFailed: true as const });
}

/** Removes mutator-only options before handing request initialization to the browser. */
function browserRequestInit(init: OrvalRequestInit): RequestInit {
  var requestInit = { credentials: 'include' as RequestCredentials, ...init };
  delete requestInit.parseErrorBody;
  return requestInit;
}

/** Executes an Orval-generated operation against the configured backend origin. */
export function orvalFetch<T>(path: string, init: OrvalRequestInit): Promise<T> {
  return request(path, init, false).then((response) => parseResponse(response) as Promise<T>);
}

/** A successful bodyless mutation must acknowledge completion with the documented status. */
export function orvalNoContent<T>(path: string, init: OrvalRequestInit): Promise<T> {
  return request(path, init, false).then((response) => {
    if (response.status !== 204) throw httpError(response);
    return undefined as T;
  });
}

/** Preserves status and cache headers for the generated conditional-avatar operation. */
export function orvalResponse<T>(path: string, init: OrvalRequestInit): Promise<T> {
  return request(path, init, true).then((response) =>
    parseResponse(response).then(
      (data) =>
        ({
          data,
          status: response.status,
          headers: response.headers,
        }) as T
    )
  );
}

/** Fetches and classifies HTTP failures before either response representation is constructed. */
function request(
  path: string,
  init: OrvalRequestInit,
  allowNotModified: boolean
): Promise<Response> {
  if (!path.startsWith('/') || path.startsWith('//')) {
    return Promise.reject(new Error('Generated API requests must use root-relative paths'));
  }

  return fetch(`${API_URL}${path}`, browserRequestInit(init)).then((response) => {
    if (!response.ok && !(allowNotModified && response.status === 304)) {
      if (init.parseErrorBody === false) return Promise.reject(httpError(response));
      return responseText(response).then(
        (body) => {
          throw httpError(response, body ? parseErrorData(body) : undefined);
        },
        () => {
          throw httpError(response, undefined, true);
        }
      );
    }
    return response;
  });
}

/** Empty cache/mutation responses must never be passed to a body parser. */
function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 304) return Promise.resolve(undefined);
  // Browser/test doubles often omit Content-Type for JSON. Known binary
  // endpoints declare their media type, so an absent header follows the JSON
  // contract used by the rest of the API.
  if (responseUsesJson(response)) {
    return response.json().catch((error: unknown) => {
      throw markResponseReadFailure(error);
    });
  }
  return response.blob().catch((error: unknown) => {
    throw markResponseReadFailure(error);
  });
}
